<template>
  <div class="relative h-full w-full bg-black">
    <img
      ref="imgRef"
      :src="src"
      alt=""
      draggable="false"
      decoding="async"
      class="block h-full w-full bg-black object-center"
      :class="[fit === 'contain' ? 'object-contain' : 'object-cover', ready ? 'opacity-100' : 'opacity-0']"
      @error="onError"
    >
  </div>
</template>

<script lang="ts" setup>
import type { TSectionFit } from '@lucimark/shared/helpers/sectionMediaFit'
import { inject, onBeforeUnmount, ref, watch } from 'vue'
import { ensureHtmlImageDecoded, isImagePredecoded } from '@/helpers/imagePredecodeCache'

const props = defineProps<{
  src: string
  /** Omitted = historical image default (cover). */
  fit?: TSectionFit
}>()

const reportHoldPaintReady = inject<((info: { type: 'image', src: string }) => void) | null>(
  'reportHoldPaintReady',
  null
)

const imgRef = ref<HTMLImageElement | null>(null)
/** Cold stills start hidden; warm/predecoded stills reveal immediately (no opacity-0 black flash). */
const ready = ref(props.src !== '' && isImagePredecoded(props.src))
let decodeGeneration = 0

function notifyPaintReady(): void {
  if (!ready.value || props.src === '') {
    return
  }
  reportHoldPaintReady?.({ type: 'image', src: props.src })
}

async function decodeCurrent(): Promise<void> {
  const gen = ++decodeGeneration
  const src = props.src
  const warm = src !== '' && isImagePredecoded(src)
  // Keep previous paint visible when the next still is already predecoded.
  if (!warm) {
    ready.value = false
  }
  else {
    ready.value = true
    notifyPaintReady()
  }
  const el = imgRef.value
  if (el == null || src === '') {
    return
  }
  const ok = await ensureHtmlImageDecoded(el, src)
  if (gen !== decodeGeneration) {
    return
  }
  ready.value = ok || el.complete
  notifyPaintReady()
}

function onError(): void {
  ready.value = false
}

watch(
  () => props.src,
  () => {
    void decodeCurrent()
  },
  { immediate: true }
)

watch(imgRef, (el) => {
  if (el != null) {
    void decodeCurrent()
  }
})

onBeforeUnmount(() => {
  decodeGeneration += 1
})
</script>
